import React, { useState } from 'react';
import Link from 'next/link';
import styled from '@emotion/styled';
import { useTheme } from '@emotion/react';
import { Theme, ThemeProps } from '@theme/base';
import { LinkItem } from '@model/iceberg';
import { PlainLinkStyles } from '@styles/common';
import { useI18NextContext } from '@components/hooks';
import { TestId } from '@components/test-ids';
import { Icon } from '@components/common/generic-icon';
import { Icons } from '@model/common/icons';

export const MAX_LINK_COUNT = 5;

/* ***************** *
 *       Types       *
 * ***************** */

export interface GenericLinkListProps {
  testId?: string;
  title: string;
  links: Array<LinkRow>;
}

export interface LinkRow {
  label: string;
  linkUrl: LinkItem;
}

/* ****************** *
 *       Styles       *
 * ****************** */

const Container: any = styled.div({});

const TitleRow: any = styled.div(({ theme }: ThemeProps) => ({
  background: theme.custom.colors.group10.lighter,
  borderBottom: `1px solid ${theme.custom.colors.group10.light}`,
  padding: theme.custom.spacing.medium,
  ['h5']: {
    margin: 0
  }
}));

const LinkRow: any = styled(TitleRow)(({ theme }: ThemeProps) => ({
  alignItems: 'center',
  background: theme.custom.colors.white,
  borderBottom: 'none',
  color: theme.custom.colors.group4.base,
  display: 'flex',
  transition: `color ${theme.custom.transitions.default}`,

  ['&:hover, &:hover i']: {
    color: theme.custom.colors.group1.base
  }
}));

const LinkLabel: any = styled.p({
  flex: 1
});

const LinkRowInnerContainer: any = styled.div(({ theme }: ThemeProps) => ({
  display: 'flex',
  flex: 1,
  justifyContent: 'space-between',
  alignItems: 'center',
  marginRight: theme.custom.spacing.medium
}));

const ViewButton = styled(LinkRow)(({ theme }: ThemeProps) => ({
  borderTop: `1px solid ${theme.custom.colors.group10.light}`,
  cursor: 'pointer',
  justifyContent: 'center',
  color: theme.custom.colors.group2.base,
  paddingBottom: 8,
  paddingTop: 8
}));

export const GenericLinkList = ({ testId, title, links }: GenericLinkListProps) => {
  const t = useI18NextContext();
  const theme: Theme = useTheme();
  const [viewMore, setViewMore] = useState(false);
  const toggleViewMore = () => setViewMore(!viewMore);
  const renderLinkRows = () => {
    const visibleLinks = viewMore ? links.slice() : links.slice(0, MAX_LINK_COUNT);
    return visibleLinks.map((link: LinkRow) => (
      <Link prefetch={false} {...link.linkUrl} key={link.label} passHref style={PlainLinkStyles}>
        <LinkRow>
          <LinkRowInnerContainer>
            <LinkLabel>{link.label}</LinkLabel>
          </LinkRowInnerContainer>
          <Icon name={Icons.CHEVRON_RIGHT} />
        </LinkRow>
      </Link>
    ));
  };
  return (
    <Container data-testid={testId || TestId.genericLinkListComponent.main}>
      <TitleRow>
        <h5>{title}</h5>
      </TitleRow>
      {renderLinkRows()}
      {links.length > MAX_LINK_COUNT && (
        <ViewButton onClick={toggleViewMore}>
          <p>{t(viewMore ? 'common__label--view-less' : 'common__label--view-more')}</p>
          <Icon
            name={viewMore ? Icons.CHEVRON_UP : Icons.CHEVRON_DOWN}
            css={{ marginLeft: theme.custom.spacing.small, color: theme.custom.colors.group2.base }}
          />
        </ViewButton>
      )}
    </Container>
  );
};
